import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "@/utils/axios";

import cls from "./PendingRequests.module.scss";

import avatarPng from "@/assets/UI/avatar.png";

function formatRelativeTime(input) {
  if (!input) return "";
  const date = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return "";

  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (sec < 45) return "just now";
  if (min < 60) return `${min} min ago`;
  if (hr < 24) return `${hr} h ago`;
  if (day === 1) return "1 day ago";
  return `${day} days ago`;
}

const PendingRequests = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyMap, setBusyMap] = useState({}); // { [requestId]: "accept" | "decline" }
  const [confirmMap, setConfirmMap] = useState({});
  const [declineConfirmMap, setDeclineConfirmMap] = useState({});

  const isPublic = location.pathname === "/lounge";
  const isFriends =
    location.pathname === "/lounge/friends-feed" ||
    location.pathname === "/lounge/friends/requests" ||
    location.pathname === "/lounge/pending-requests";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const pendingCount = requests.length;

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const res = await api.get("/v1/friends/requests/incoming");
      setRequests(res.data?.requests || []);
    } catch (err) {
      console.error("Failed to load incoming friend requests", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleRespond = useCallback(async (id, action) => {
    if (!id || (action !== "accept" && action !== "decline")) return;
    setBusyMap((prev) => ({ ...prev, [id]: action }));

    try {
      await api.post(`/v1/friends/requests/${id}/${action}`);
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error(`Failed to ${action} friend request`, err);
      // Axios interceptor should show toast
    } finally {
      setBusyMap((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);

  const handleViewProfile = useCallback(
    (memberId) => {
      if (!memberId) return;
      navigate(`/lounge/member-profile/${memberId}`);
    },
    [navigate],
  );
  const handleAskConnectionType = useCallback((id) => {
    if (!id) return;
    setConfirmMap((prev) => ({ ...prev, [id]: true }));
  }, []);

  const handleConfirmConnection = useCallback(async (id, type) => {
    if (!id) return;
    if (type !== "online_only" && type !== "know_personally") return;

    setBusyMap((prev) => ({ ...prev, [id]: "accept" }));
    try {
      await api.post(`/v1/friends/requests/${id}/accept`, {
        connectionType: type,
      });
      setRequests((prev) => prev.filter((r) => r.id !== id));
      setConfirmMap((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (err) {
      console.error("Failed to accept friend request with connectionType", err);
      // axios interceptor shows toast
    } finally {
      setBusyMap((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);
  const handleAskDecline = useCallback((id) => {
    if (!id) return;
    setDeclineConfirmMap((prev) => ({ ...prev, [id]: true }));
  }, []);

  const handleConfirmDecline = useCallback(
    async (id) => {
      if (!id) return;
      // reuse your existing decline logic
      setDeclineConfirmMap((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await handleRespond(id, "decline");
    },
    [handleRespond],
  );

  const handleCancelDecline = useCallback((id) => {
    if (!id) return;
    setDeclineConfirmMap((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        {/* Main layout */}
        <div className={cls.layout}>
          {/* Left column – friends + groups (desktop only) */}
          <div className={cls.leftColumn}></div>

          {/* Right column – friend requests list */}
          <div className={cls.rightColumn}>
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("pendingRequests.title")}</h2>
                <span className={cls.cardMeta}>
                  {t("pendingRequests.countLabel", { count: pendingCount })}
                </span>
              </div>

              {loading && (
                <p className={cls.cardMeta}>{t("pendingRequests.loading")}</p>
              )}

              {!loading && loadError && (
                <div>
                  <p className={cls.cardMeta}>{t("pendingRequests.error")}</p>
                  <button
                    type="button"
                    className={cls.linkButton}
                    onClick={fetchRequests}
                  >
                    {t("pendingRequests.retry")}
                  </button>
                </div>
              )}

              {!loading && !loadError && pendingCount === 0 && (
                <div>
                  <p className={cls.cardMeta}>
                    {t("pendingRequests.emptyTitle")}
                  </p>
                  <p className={cls.cardMeta}>
                    {t("pendingRequests.emptyText")}
                  </p>
                </div>
              )}

              {!loading && !loadError && pendingCount > 0 && (
                <>
                  {requests.map((req) => {
                    const profile = req.fromUserProfile || {};
                    const avatarUrl =
                      profile.avatar?.url || profile.avatar?.key || avatarPng;

                    const memberTypeKey = profile.memberType || "unknown";
                    const memberTypeLabel = t(
                      `pendingRequests.memberType.${memberTypeKey}`,
                      { defaultValue: profile.memberType || "" },
                    );

                    const genderKey = profile.gender || "unknown";
                    const genderLabel = t(
                      `pendingRequests.genderLabels.${genderKey}`,
                      { defaultValue: profile.gender || "" },
                    );

                    const busyState = busyMap[req.id];

                    return (
                      <article key={req.id} className={cls.postCard}>
                        <header className={cls.postHeader}>
                          <img
                            src={avatarUrl}
                            alt={t("pendingRequests.avatarAlt")}
                            className={cls.postAvatar}
                          />
                          <div className={cls.postHeaderText}>
                            <span className={cls.postAuthor}>
                              {profile.username ||
                                t("pendingRequests.unknownUser")}
                            </span>
                            <span className={cls.postMeta}>
                              {memberTypeLabel && `${memberTypeLabel} · `}
                              {genderLabel && `${genderLabel} · `}
                              {formatRelativeTime(req.createdAt)}
                            </span>
                          </div>
                        </header>

                        {req.note && (
                          <p className={cls.postText}>
                            <strong>{t("pendingRequests.noteLabel")} </strong>
                            {req.note}
                          </p>
                        )}

                        <footer className={cls.postFooter}>
                          <button
                            type="button"
                            className={`${cls.postAction} ${cls.postActionAccept}`}
                            onClick={() => handleAskConnectionType(req.id)}
                            disabled={!!busyState}
                          >
                            {t("pendingRequests.actions.accept")}
                          </button>

                          <button
                            type="button"
                            className={`${cls.postAction} ${cls.postActionDecline}`}
                            onClick={() => handleAskDecline(req.id)}
                            disabled={!!busyState}
                          >
                            {t("pendingRequests.actions.decline")}
                          </button>

                          <button
                            type="button"
                            className={`${cls.postAction} ${cls.postActionView}`}
                            onClick={() => handleViewProfile(profile.memberId)}
                            disabled={!profile.memberId}
                          >
                            {t("pendingRequests.actions.viewProfile")}
                          </button>
                        </footer>
                        {confirmMap[req.id] && (
                          <div className={cls.confirmRow}>
                            <p className={cls.confirmQuestion}>
                              {t("pendingRequests.knowPersonally.question")}
                            </p>
                            <div className={cls.confirmActions}>
                              <button
                                type="button"
                                className={`${cls.confirmButton} ${cls.confirmButtonYes}`}
                                onClick={() =>
                                  handleConfirmConnection(
                                    req.id,
                                    "know_personally",
                                  )
                                }
                                disabled={!!busyState}
                              >
                                {t("pendingRequests.knowPersonally.yes")}
                              </button>
                              <button
                                type="button"
                                className={`${cls.confirmButton} ${cls.confirmButtonNo}`}
                                onClick={() =>
                                  handleConfirmConnection(req.id, "online_only")
                                }
                                disabled={!!busyState}
                              >
                                {t("pendingRequests.knowPersonally.no")}
                              </button>
                            </div>
                          </div>
                        )}
                        {declineConfirmMap[req.id] && (
                          <div className={cls.confirmRow}>
                            <p className={cls.confirmQuestion}>
                              {t("pendingRequests.declineConfirm.question")}
                            </p>
                            <div className={cls.confirmActions}>
                              <button
                                type="button"
                                className={`${cls.confirmButton} ${cls.confirmButtonYes}`}
                                onClick={() => handleConfirmDecline(req.id)}
                                disabled={!!busyState}
                              >
                                {t("pendingRequests.declineConfirm.yes")}
                              </button>
                              <button
                                type="button"
                                className={`${cls.confirmButton} ${cls.confirmButtonNo}`}
                                onClick={() => handleCancelDecline(req.id)}
                                disabled={!!busyState}
                              >
                                {t("pendingRequests.declineConfirm.no")}
                              </button>
                            </div>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingRequests;
